<template>
  <div class="meet-step">
    <div class="reader-intro">
      <div class="reader-avatar">
        <img v-if="portraitUrl" :src="portraitUrl" />
        <div v-else class="avatar-placeholder">&#10022;</div>
      </div>
      <div class="reader-name">{{ displayName }}</div>
    </div>

    <!-- Auto-redirects to Studio after brief moment -->
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import { store, static_store } from '@/store'
import { useReaderConversation } from '@/composables/useReaderConversation'

const props = defineProps({
  readerSubdomain: String,
  readerName: String,
  chosenName: String,
  portraitUrl: String,
})
const emit = defineEmits(['enter-studio', 'context-signal'])

const wsMessages = ref([])
const connected = ref(false)
const error = ref(false)

// Prioritize: chosenName (what user picked) > readerName (from claim API) > session response
const fetchedName = ref('')

const displayName = computed(() => {
  return props.chosenName || props.readerName || fetchedName.value || ''
})

const {
  connectWebSocket,
  handleWebSocketMessages,
  getWelcomeMessage,
} = useReaderConversation()

// Watch for new WebSocket messages and route them to the left chat panel
watch(wsMessages, (msgs) => {
  if (msgs.length > 0) {
    const latest = msgs[msgs.length - 1]
    const text = typeof latest === 'string' ? latest : latest?.text || ''
    if (text.trim()) {
      emit('context-signal', {
        panel: 'studio',
        view: 'meet_reader',
        interaction: 'reader_message',
        data: { text: text.trim(), name: displayName.value },
      })
    }
  }
}, { deep: true })

onMounted(async () => {
  try {
    const token = localStorage.getItem('authToken')

    const sessionUrl = `${static_store.apiUrl}/reader/start-session/`
      + `?subdomain=${encodeURIComponent(props.readerSubdomain)}`
      + `&session_id=`
      + `&querent_id=${encodeURIComponent(store.querent_id || '')}`
      + `&token=${encodeURIComponent(token || '')}`

    const resp = await axios.get(sessionUrl)
    store.session_id = resp.data.session_id
    store.conversation_state = resp.data.reader_state
    store.current_reader = resp.data.reader_id
    store.current_reader_name = resp.data.reader_name
    fetchedName.value = resp.data.reader_name

    const state = { store, static_store }
    await connectWebSocket(state)
    handleWebSocketMessages(wsMessages)

    await getWelcomeMessage(store.session_id, token)
    connected.value = true

    // Auto-navigate to Studio after a brief moment
    setTimeout(() => emit('enter-studio'), 2000)
  } catch (e) {
    error.value = true
    // Still redirect to Studio even on error
    setTimeout(() => emit('enter-studio'), 3000)
  }
})
</script>

<style scoped>
.meet-step {
  color: #E8D8FF;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 40px 0 16px;
}

.reader-intro {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.reader-avatar {
  width: 160px;
  height: 160px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(201, 169, 78, 0.3);
  box-shadow: 0 4px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(201, 169, 78, 0.08);
}

.reader-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: rgba(201, 169, 78, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: #C9A94E;
}

.reader-name {
  font-family: 'Cinzel', serif;
  font-size: 26px;
  font-weight: 600;
  color: #FFDFA3;
  letter-spacing: 0.5px;
}

.meet-actions {
  width: 100%;
}

.studio-btn {
  width: 100%;
  padding: 16px;
  background: rgba(125, 232, 204, 0.1);
  border: 1px solid rgba(125, 232, 204, 0.3);
  border-radius: 12px;
  color: #7DE8CC;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
}

.studio-btn:hover {
  background: rgba(125, 232, 204, 0.18);
}
</style>
